import { Button, cn, Divider, Stack } from 'component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IndicatorFactType, PlotReportFactElement, PlotReportFactLabelNameEnum } from '@/api/rest/resources/types/fact';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { NCCard2SectionLayout } from '@/components/NCCard/NCCard';
import {
  Explainer,
  ExplainerBody,
  ExplainerContent,
  ExplainerCopy,
  ExplainerTitle,
  ExplainerTrigger,
} from '@/components/NCExplainers/Explainer';
import { NCFactCapsule } from '@/components/NCFactCapsule/NCFactCapsule';
import { GradientScale } from '@/components/widgets/o/Gradient/Gradient';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';
import { PlotCardWrapper } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/PlotCardWrapper';
import { PlotIndicatorCard } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/PlotIndicatorCard';
import { isNullish } from '@/utils/booleans';

type GradientFact = {
  min: number | null | undefined;
  max: number | null | undefined;
  value: number | null | undefined;
};

type FactMapping = {
  name: string;
  tooltip?: string;
  min?: number;
  max?: number;
};
type GroupFact = GradientFact & Pick<FactMapping, 'tooltip'> & { unit?: string };

type CoastalEcosystemConditionProps = {
  factsMapping: FactMapping[];
  groupingLabel?: string;
} & Record<string, PlotReportFactElement<unknown>[] | FactMapping[] | string | undefined>;

export const CoastalEcosystemCondition = (props: CoastalEcosystemConditionProps) => {
  const { t } = useTranslation();

  return (
    <PlotCardWrapper name={t('shared.ncaDetail.details.coastalEcosystemCondition.labels.heading')}>
      <CoastalEcosystemConditionCard {...props} />
    </PlotCardWrapper>
  );
};

const CoastalEcosystemConditionCard = ({
  factsMapping,
  groupingLabel = 'ecosystem',
  ...factsByName
}: CoastalEcosystemConditionProps) => {
  const { t } = useTranslation();

  const facts = factsMapping.flatMap(
    (mapping) => (factsByName[mapping.name] as PlotReportFactElement<unknown>[] | undefined) ?? [],
  );

  const groups = Array.from(
    new Set(
      facts?.flatMap((f) => {
        return f?.labels?.find((l) => l.name === groupingLabel)?.value ?? [];
      }),
    ),
  ) as string[];

  const groupToFactsMap = Object.fromEntries(
    groups.map((group) => {
      const groupData = Object.fromEntries(
        factsMapping.flatMap((fact) => {
          const factValues = getGroupFacts(facts, fact.name, groupingLabel, group, factsMapping);

          if (isNullish(factValues?.value)) {
            return [];
          }
          return [[fact.name, factValues] as const];
        }),
      );
      return [group, groupData];
    }),
  );

  // TODO: MVP-9692 update fact types to custom data
  const hasData = facts?.map((fact) => fact?.value).some((value) => !isNullish(value));
  if (!hasData) {
    return null;
  }

  const cardProps = {
    'data-testid': 'index-card-nca',
    explainer: <CoastalEcosystemConditionExplainer />,
  };

  const TITLE = t(`shared.ncaDetail.details.coastalEcosystemCondition.labels.heading`);

  const headerProps = {
    title: TITLE,
    className: 'lg:grid-cols-[1fr_1fr]',
  };
  const footerProps = {
    logos: ['tnfd', 'gri', 'sbtn', 'cdp', 'csrd', 'npi', 'eudr', 'sai'],
  };

  const graphProps = {
    children: <GraphTile defaultTab={Object.keys(groupToFactsMap)[0]} chartData={groupToFactsMap} groups={groups} />,
  };

  const nCCard2SectionLayoutProps = {
    showDivider: false,
  };

  return (
    <PlotIndicatorCard
      cardProps={cardProps}
      headerProps={headerProps}
      footerProps={footerProps}
      graphProps={graphProps}
      nCCard2SectionLayoutProps={nCCard2SectionLayoutProps}
    />
  );
};

const GraphTile = ({
  defaultTab,
  chartData,
  groups,
}: {
  defaultTab: string | undefined;
  chartData: Record<string, Record<string, GroupFact>>;
  groups: string[];
}) => {
  const { t } = useTranslation();
  const [selectedGroup, setSelectedGroup] = useState<string | undefined>(defaultTab);

  if (isNullish(selectedGroup)) return null;

  const completeGradients: { factType: string; facts: GroupFact }[] = [];
  const standAloneFacts: { factType: string; facts: GroupFact }[] = [];
  Object.entries(chartData[`${selectedGroup}`] as Record<string, GroupFact>).forEach(([factType, facts]) => {
    const isCompleteGradient = !isNullish(facts?.min) && !isNullish(facts?.max) && !isNullish(facts?.value);
    const factName = factType.replace(/^insitu_/, '');
    if (isCompleteGradient) {
      completeGradients.push({ factType: factName, facts });
    }

    if (!isCompleteGradient && !isNullish(facts?.value)) {
      standAloneFacts.push({ factType: factName, facts });
    }
  });

  return (
    <Stack className='w-full gap-4 py-8'>
      <Stack className='w-full grid grid-cols-5 border-b border-divider'>
        {groups.map((group) => (
          <Button
            key={group}
            variant='text'
            className={cn(
              'px-4 py-2 rounded-none typography-body2',
              selectedGroup === group ? 'bg-primary-12 text-white' : 'bg-white text-text-secondary',
            )}
            onClick={() => setSelectedGroup(group)}
          >
            <span className='capitalize'>{group.replace(/_/g, ' ')}</span>
          </Button>
        ))}
      </Stack>
      <NCCard2SectionLayout showDivider={completeGradients.length > 0 && standAloneFacts.length > 0}>
        {completeGradients.length === 0 && standAloneFacts.length === 0 && (
          <div className='typography-body1 w-full text-center py-4 text-text-secondary'>
            {t('shared.ncaDetail.details.coastalEcosystemCondition.labels.noDataForGroup', { group: selectedGroup })}
          </div>
        )}
        <Stack spacing={3}>
          {completeGradients.map(({ factType, facts }, i) => (
            <>
              <GradientScale
                key={factType}
                {...facts}
                title={factType.replace(/_/g, ' ')}
                tooltip={facts?.tooltip}
                unit={facts?.unit ?? UnitEnum.none}
              />
              {i !== completeGradients.length - 1 && <Divider />}
            </>
          ))}
        </Stack>
        <Stack>
          <div className='grid grid-cols-2 py-2 gap-6 h-fit w-full'>
            {standAloneFacts.map(({ factType, facts }) => (
              <NCFactCapsule
                key={factType}
                label={factType.replace(/_/g, ' ')}
                fact={
                  {
                    value: facts.value,
                    unit: facts?.unit ?? UnitEnum.none,
                  } as PlotReportFactElement<unknown>
                }
              />
            ))}
          </div>
        </Stack>
      </NCCard2SectionLayout>
    </Stack>
  );
};

const CoastalEcosystemConditionExplainer = () => {
  const { t } = useTranslation();

  return (
    <Explainer>
      <ExplainerTrigger>{t('global.howDoWeMeasureThis')}</ExplainerTrigger>
      <ExplainerContent>
        <ExplainerTitle>
          {t(`shared.ncaDetail.details.coastalEcosystemCondition.explainer.howDoWeMeasureThis.title`)}
        </ExplainerTitle>
        <Stack className='items-start pb-6'>
          <ExplainerBody>
            <ExplainerCopy
              body={t(`shared.ncaDetail.details.coastalEcosystemCondition.explainer.howDoWeMeasureThis.body`)}
            />
          </ExplainerBody>
        </Stack>
      </ExplainerContent>
    </Explainer>
  );
};

const getGroupFacts = (
  facts: PlotReportFactElement<unknown>[] | undefined,
  factName: IndicatorFactType,
  groupName: string,
  groupValue: string,
  factMappings: FactMapping[],
): GroupFact => {
  const factMapping = factMappings.find((mapping) => mapping.name === factName);
  const baseFact = getFactFromFactArray<number>(facts, factName, { [groupName]: groupValue });

  return {
    min:
      factMapping?.min ??
      getFactFromFactArray<number>(facts, factName, {
        [PlotReportFactLabelNameEnum.statistic]: 'min',
        [groupName]: groupValue,
      })?.value,
    max:
      factMapping?.max ??
      getFactFromFactArray<number>(facts, factName, {
        [PlotReportFactLabelNameEnum.statistic]: 'max',
        [groupName]: groupValue,
      })?.value,
    value: baseFact?.value,
    unit: baseFact?.unit,
    tooltip: factMapping?.tooltip,
  };
};
